import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  Component,
  Input,
  OnInit,
  SimpleChanges,
  ChangeDetectionStrategy,
  inject
} from "@angular/core";
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import * as echarts from "echarts";
import { v4 as uuidv4 } from "uuid";
import { SmartComponentService } from "src/components/shared-components/service/smart-component.service";
import { LoaderService } from "src/service/loader.service";
import { GenericCounterGraphData } from "../../class/generic-counter-graph-data";
import { CounterConsumptionMonthlyComparison } from "../../class/counter-consumption-monthly-comparison";
import { AlertService } from "src/service/alert.service";
import { LocaleService } from "../../service/locale.service";
import { Platform, IonButton, IonIcon } from "@ionic/angular";

import { SmartPaletteCounter } from "../../class/smart-palette";
import { LoggerService } from "src/service/logger.service";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { NgxEchartsDirective } from "ngx-echarts";
import { ErrorChartComponent } from "../error-chart/error-chart.component";

@Component({
    selector: "app-chart-bar",
    templateUrl: "./chart-bar.component.html",
    styleUrls: ["./chart-bar.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [IonButton, IonIcon, NgxEchartsDirective, ErrorChartComponent, TranslatePipe]
})

/**
 * Chart bar componet
 */
export class ChartBarComponent implements OnInit {
  private readonly _translateService = inject(TranslateService);
  private readonly _platform = inject(Platform);
  private readonly _alertService = inject(AlertService);
  private readonly _loggerService = inject(LoggerService);

  private _logContext: string = "Chart-bar.component";

  public uniqueId: string;
  public showChart: boolean = false;
  public barChart: any;
  public isMobileDevice: boolean = false;
  private readonly _localeService = inject(LocaleService);
  private _currentYear: number = new Date().getFullYear();
  private _lastYear: number = this._currentYear - 1;
  private _lblJanAbbrev: string = "";
  private _lblFebAbbrev: string = "";
  private _lblMarAbbrev: string = "";
  private _lblAprAbbrev: string = "";
  private _lblMayAbbrev: string = "";
  private _lblJunAbbrev: string = "";
  private _lblJulAbbrev: string = "";
  private _lblAugAbbrev: string = "";
  private _lblSepAbbrev: string = "";
  private _lblOctAbbrev: string = "";
  private _lblNovAbbrev: string = "";
  private _lblDecAbbrev: string = "";
  private _lastYearValues: string[] = [];
  private _currentYearValues: string[] = [];
  private _chartInitialized: boolean = false;
  private _intervalId: any;
  private _dataReady: boolean = false;

  @Input() skillVisible: boolean = false;
  @Input() enableSwipe: boolean = false;
  @Input() dataChart: GenericCounterGraphData = null;
  @Input() dataChartCSV: string = null;
  @Input() errorChart: boolean = false;
  @Input() um: string = "";
  @Input() smartPalette: SmartPaletteCounter = null;
  @Input() chartName: string = "";
  @Input() counterName: string = "";

  constructor() {
    this.isMobileDevice = this._platform.is("ios") || this._platform.is("android");

    // The texts of the chart are written in the code: write them again in the new language
    this._translateService.onLangChange
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.translateMonth());
  }

  ngOnInit() {
    this.translateMonth();
    this.uniqueId = uuidv4();
  }

  ngAfterViewInit() {
    this._intervalId = setInterval(() => {
      if (this.skillVisible && this._dataReady && !this._chartInitialized) {
        this.initChart();
        this._chartInitialized = true;
        clearInterval(this._intervalId);
      }
    }, 100);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.skillVisible) {
      this.showChart = true;
    } else {
      this.showChart = false;
    }
    if (changes.dataChart) {
      this._dataReady =
        this.dataChart &&
        Array.isArray(this.dataChart.data) &&
        this.dataChart.data.length > 0;
    }
  }

  /**
   * Recharge month with translation
   */
  private translateMonth() {
    const monthsAbbrev = Array.from({ length: 12 }, (_, month) =>
      this._localeService.formatDate(new Date(2000, month, 1), "monthShort").toUpperCase().replace(".", "")
    );

    this._lblJanAbbrev = monthsAbbrev[0];
    this._lblFebAbbrev = monthsAbbrev[1];
    this._lblMarAbbrev = monthsAbbrev[2];
    this._lblAprAbbrev = monthsAbbrev[3];
    this._lblMayAbbrev = monthsAbbrev[4];
    this._lblJunAbbrev = monthsAbbrev[5];
    this._lblJulAbbrev = monthsAbbrev[6];
    this._lblAugAbbrev = monthsAbbrev[7];
    this._lblSepAbbrev = monthsAbbrev[8];
    this._lblOctAbbrev = monthsAbbrev[9];
    this._lblNovAbbrev = monthsAbbrev[10];
    this._lblDecAbbrev = monthsAbbrev[11];
  }

  /**
   * Elaborate graph data
   */
  private async elaborateGraphData() {
    try {
      //Replace value with dataGraph
      if (Array.isArray(this.dataChart.data)) {
        this.dataChart.data.forEach((item) => {
          let castedItem = item as CounterConsumptionMonthlyComparison;

          if (castedItem.month !== undefined) {
            this._lastYearValues.push(castedItem.lastYearValue);
            this._currentYearValues.push(castedItem.currentYearValue);
          }
        });
      }
    } catch {
      await this._alertService.presentAlertSomethingWrong();
    }
  }

  /**
   * Initialize chart
   */
  private async initChart(): Promise<void> {
    await this.elaborateGraphData();
    const chartDom = document.getElementById(this.uniqueId) as HTMLElement;

    if (chartDom) {
      this.barChart = echarts.init(chartDom);

      const option = {
        tooltip: {
          trigger: "axis",
          textStyle: {
            color: "#000000",
          },
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          formatter: function (params) {
            let tooltipText = params[0].name + "<br/>";
            params.forEach(function (item) {
              const marker =
                '<span style="display:inline-block;margin-right:5px;width:9px;height:9px;border-radius:50%;border:1px solid #888;background-color:' +
                item.color +
                ';"></span>';
              tooltipText +=
                marker +
                item.seriesName +
                ": " +
                item.value.replace(".", ",") +
                " " +
                this.um +
                "<br/>";
            }, this);
            return tooltipText;
          }.bind(this),
        },
        xAxis: {
          type: "category",
          boundaryGap: true,
          data: [
            this._lblJanAbbrev,
            this._lblFebAbbrev,
            this._lblMarAbbrev,
            this._lblAprAbbrev,
            this._lblMayAbbrev,
            this._lblJunAbbrev,
            this._lblJulAbbrev,
            this._lblAugAbbrev,
            this._lblSepAbbrev,
            this._lblOctAbbrev,
            this._lblNovAbbrev,
            this._lblDecAbbrev,
          ],
          axisLine: {
            lineStyle: {
              color: this.smartPalette?.mainLableColor || "#ffffff",
            },
          },
          axisLabel: {
            interval: 0,
            color: this.smartPalette?.mainLableColor || "#ffffff",
            fontSize: 12,
            rotate: 90
          }
        },
        yAxis: {
          type: "value",
          axisLine: {
            onZero: true,
            show: true,
            lineStyle: {
              color: this.smartPalette?.mainLableColor || "#FFFFFF",
              width: 2,
            },
          },
          axisLabel: {
            formatter: '{value} ' + this.um,
            color: this.smartPalette?.mainLableColor || "#ffffff",
          },
          splitLine: {
            show: true,
            lineStyle: {
              color:
                this.smartPalette?.secondaryLableColor ||
                "rgba(255, 255, 255, 0.7)",

              width: 1,
              type: "solid",
            },
          },
        },
        legend: {
          data: [this._lastYear.toString(), this._currentYear.toString()],
          textStyle: {
            color: this.smartPalette?.mainLableColor || "#ffffff",
          },
          orient: "horizontal",
          top: this.isMobileDevice ? "top" : 10,
          left: this.isMobileDevice ? "center" : 0,
          itemGap: 20,
        },
        series: [
          {
            name: this._lastYear.toString(),
            type: "bar",
            itemStyle: {
              color: this.smartPalette?.graphBar1Color || "#ffffff",
            },
            data: this._lastYearValues,
          },
          {
            name: this._currentYear.toString(),
            type: "bar",
            itemStyle: {
              color: this.smartPalette?.graphBar2Color || "#035E91",
            },
            data: this._currentYearValues,
          },
        ],
        grid: {
          left: 60,
          bottom: 40
        },
      };
      this.barChart.setOption(option);

      // Resizing the chart if you change window size without reloading the page
      window.addEventListener("resize", () => {
        this.barChart.resize();
      });

    } else {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "initChart()",
        'Element with id "area-chart" not found.'
      );
    }
  }

  /**
   * Handle click to export CSV
   */
  public onClickExportCSV() {
    const fileName = (this.counterName + "_" + this.chartName).replace(/\s+/g, '_').toLocaleLowerCase() + ".csv"
    const blob = new Blob([this.dataChartCSV], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;

    link.click();

    window.URL.revokeObjectURL(url);
  }
}
